'use client';

import { Copy, ExternalLink, Link2 } from 'lucide-react';

import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { copyToClipboard } from '@/utils/clipboard';

type PrV2UserNameLabelProps = {
  login: string | null | undefined;
  displayText?: string | null | undefined;
  className?: string;
};

export function PrV2UserNameLabel({ login, displayText, className }: PrV2UserNameLabelProps) {
  const name = login || 'unknown';
  const label = displayText || name;
  const profileUrl = `https://github.com/${encodeURIComponent(name)}`;

  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <ContextMenu>
          <TooltipTrigger asChild>
            <ContextMenuTrigger asChild>
              <button
                type="button"
                className={cn(
                  'cursor-pointer font-medium text-zinc-200 transition-colors hover:text-zinc-100',
                  className,
                )}
                onClick={() =>
                  void copyToClipboard(name, {
                    successMessage: 'Copied username',
                    errorMessage: 'Failed to copy',
                  })
                }
              >
                {label}
              </button>
            </ContextMenuTrigger>
          </TooltipTrigger>
          <TooltipContent>Click to copy username. Right-click for more actions.</TooltipContent>
          <ContextMenuContent>
            <ContextMenuItem
              onClick={() =>
                void copyToClipboard(name, {
                  successMessage: 'Copied username',
                  errorMessage: 'Failed to copy',
                })
              }
            >
              <Copy className="mr-2 size-3.5 text-zinc-400" />
              Copy username
            </ContextMenuItem>
            <ContextMenuItem
              onClick={() =>
                void copyToClipboard(profileUrl, {
                  successMessage: 'Copied profile URL',
                  errorMessage: 'Failed to copy',
                })
              }
            >
              <Link2 className="mr-2 size-3.5 text-zinc-400" />
              Copy GitHub profile URL
            </ContextMenuItem>
            <ContextMenuItem asChild>
              <a href={profileUrl} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="mr-2 size-3.5 text-zinc-400" />
                Open GitHub profile
              </a>
            </ContextMenuItem>
          </ContextMenuContent>
        </ContextMenu>
      </Tooltip>
    </TooltipProvider>
  );
}
